const { windowLoaded, documentReady } = require('@grpn/document-states');
const domConfigHelper = require('../../../common/frontend/helpers/dom_config');
const PaginationSeoLinks = require('../../../pagination/frontend/helpers/seo_links');
const DataFetcher = require('./helpers/data-fetcher');
const Spinner = require('./helpers/spinner');
const PaginationRenderer = require('./helpers/pagination-renderer');
const arrangeCards = require('./helpers/card-arranger');
const carouselScrollPositionsHelper = require('../../../common/frontend/helpers/carousel_scroll_positions');
const urlUpdate = require('../../../common/frontend/helpers/url_update');
const QueryParams = require('../../../common/frontend/helpers/params');
const createUrlSearchParams = require('@grpn/filters/lib/create_url_search_params');
const scrollPositionHelper = require('../../../common/frontend/helpers/scroll_position');
const facetIds = require('@grpn/filters/lib/facet_ids');
const gtmHelper = require('../../../common/frontend/helpers/gtm');
const adHelper = require('../../../ads/helpers/ad-helper');
const CoreCoupons = require('@grpn/coupons-components/core-coupons-desktop');
const Pagination = require('../../../common/components/pagination');
const { render } = require('preact');

const {
  MAP_ID,
  BBOX_ID,
  TOPCATEGORY_ID,
  CATEGORY_ID,
  SUBCATEGORY_ID,
  SUBCATEGORY_2_ID,
  SUBCATEGORY_3_ID,
  SUBCATEGORY_4_ID,
} = facetIds;
const categoryParams = [
  TOPCATEGORY_ID,
  CATEGORY_ID,
  SUBCATEGORY_ID,
  SUBCATEGORY_2_ID,
  SUBCATEGORY_3_ID,
  SUBCATEGORY_4_ID,
];
const PAGE_PARAM = 'page';
const SELECTORS = {
  CARDS: '#pull-deal-feed',
  NO_CARDS: '#pull-no-cards',
  NO_CARDS_GETAWAYS: '.getaways_notification',
  NO_CARDS_GOODS: '#pull-no-cards-goods',
  NO_MAP_CARDS: '#pull-no-map-cards',
  PAGINATION: '#pull-pagination',
};

const context = {
  initialize() {
    documentReady.then(() => context.onReady());
    windowLoaded.then(() => context.onLoad());
  },
  setDefaults() {
    context.domConfig = domConfigHelper.get() || {};
  },
  updateMapButton() {
    const {
      desktopFilters,
      filtersRendered,
      handleMapState,
      domConfig,
      showMapButton,
    } = context;
    if (!desktopFilters) {
      return;
    }

    if (!filtersRendered) {
      return;
    }

    if (domConfig.showCX90Desktop) {
      const event = new CustomEvent('onMapRefinementUpdate', {
        detail: {
          shouldShowMapRefinement: showMapButton,
        },
      });

      document.dispatchEvent(event);
    }

    if (showMapButton) {
      if (!domConfig.showCX90Desktop) {
        desktopFilters.showRefinement(MAP_ID);
      }
    } else {
      if (!domConfig.showCX90Desktop) {
        desktopFilters.resetRefinement(MAP_ID);
        desktopFilters.hideRefinement(MAP_ID);
      }
      const searchParams = new URLSearchParams(location.search);
      searchParams.delete(MAP_ID);
      const stringifiedSearchParams = searchParams.toString();
      const urlSearchParams = stringifiedSearchParams.length
        ? `?${stringifiedSearchParams}`
        : '';
      const updatedUrl = `${window.location.pathname}${urlSearchParams}`;
      urlUpdate.update(updatedUrl);
      handleMapState(searchParams);
    }
  },
  initializeObjects() {
    const { showMapButton } = context.domConfig;
    context.mapContainerElement = document.querySelector('.pull-map-container');
    context.showMapButton = showMapButton;
    context.spinner = new Spinner();
    context.dataFetcher = new DataFetcher(context.domConfig);
    context.paginationRenderer = new PaginationRenderer();
    context.updateUrlWhenNoCards();
  },
  getPageNumberFromUrl(url) {
    let page = 1;

    const queryParams = url.split('?')[1];

    if (queryParams && queryParams.length) {
      const queryParamHash = Groupon.Utils.unparam(queryParams);

      if (queryParamHash.page) {
        page = queryParamHash.page;
      }
    }

    return page;
  },
  handleOnPopState() {
    const newUrlParams = Groupon.Utils.unparam();

    context.fetchCards(newUrlParams);
    context.dataFetcher
      .fetchFacets()
      .then(context.handleFacetsUpdatedSuccess)
      .catch(context.handleFacetsUpdatedError);

    context.handleMapState(newUrlParams);
  },
  attachListeners() {
    window.addEventListener('popstate', context.handleOnPopState);
  },
  lazyLoadAssets() {
    import(
      /* webpackChunkName: "browse-card-ui" */ 'grpn-card-ui/frontend/init'
    ).then(context.onLazyLoadedCardUiJavascript);

    import(
      /* webpackChunkName: "browse-desktop-filters" */ '@grpn/filters/dist/desktop'
    ).then(({ default: FiltersUi }) => {
      window.FiltersUi = FiltersUi;
      context.onLazyLoadedFiltersJavascript();
    });
  },
  onReady() {
    PaginationSeoLinks.set();
    document.body.classList.add('pull-app-is-ready');
  },
  onLoad() {
    context.setDefaults();
    context.initializeObjects();
    context.lazyLoadAssets();
    context.restorePreviousState();
    context.attachListeners();
    context.arrangeCards();
    context.loadCoreCoupons();
    gtmHelper.manageUpdateGtmDataTopFiveDeals();
    document.body.classList.add('pull-app-is-loaded');
  },
  lazyLoadRemainingDeals() {},
  onLazyLoadedCardUiJavascript() {
    window.CardUI.init();
    window.GIG.Modal.init();
    document.body.classList.add('pull-app-card-ui-js-lazy-loaded');
  },
  onLazyLoadedFiltersJavascript() {
    context.breadcrumbsContainer = document.getElementById(
      'breadcrumbs-container'
    );

    context.pageHeaderTitleContainer = document.getElementById(
      'page-header-title-container'
    );

    context.leftHandContainer = document.getElementById('left-hand-filters');

    context.filtersHeaderRowContainer =
      document.getElementById('filters-header-row');

    context.featuredFiltersContainer = document.getElementById(
      'featured-filters-ui-container'
    );

    context.bookingFiltersContainer = document.getElementById(
      'booking-filters-ui-container'
    );

    context.getawaysSearchContainer = document.getElementById(
      'getaways-search-filters-ui-container'
    );

    context.exposedCategoriesContainer = document.getElementById(
      'pull-desktop-exposed-categories'
    );

    context.desktopFilters = new window.FiltersUi();

    context.dataFetcher
      .fetchFacets()
      .then(context.handleFacetsFetchedSuccess)
      .catch(context.handleFacetsFetchedError);

    document.addEventListener(
      'refinementsUpdated',
      context.handleRefinementsUpdated
    );

    document.addEventListener('filtersRendered', context.handleFiltersRendered);
  },
  handleFiltersRendered() {
    context.filtersRendered = true;
    context.updateMapButton();
  },
  renderDealCount(pagination = {}) {
    const { count } = pagination;
    const { desktopFilters } = context;
    if (!count || !desktopFilters) {
      return;
    }
    desktopFilters.renderDealCount(count);
  },
  isNoResults(pagination) {
    if (!context.hasNoCards()) {
      return false;
    }
    if (!pagination) {
      return true;
    }
    const { count } = pagination;
    return !count;
  },
  enableStickyFilters() {
    if (context.domConfig.showCX90Desktop) {
      return;
    }

    context.filtersObserver = new IntersectionObserver(
      context.onScrollStickyFilters
    );
    context.filtersObserver.observe(document.querySelector('.header-position'));
    context.scrollStickyFilterSheaderElement =
      document.querySelector('.header-container');
    context.scrollStickyFilterPlaceholderElement = document.querySelector(
      '.header-placeholder'
    );
  },
  onScrollStickyFilters(entries) {
    const headerPositionEntry = entries[0];

    if (!headerPositionEntry) {
      return;
    }

    const { isIntersecting } = headerPositionEntry;
    const placeholderElement = context.scrollStickyFilterPlaceholderElement;
    const filterHeaderElement = context.scrollStickyFilterSheaderElement;
    const offsetMagicNumber = 26;

    placeholderElement.style.height = `${
      filterHeaderElement.offsetHeight + offsetMagicNumber
    }px`;

    if (isIntersecting) {
      filterHeaderElement.classList.remove('sticky');
      placeholderElement.style.position = 'absolute';
    } else {
      filterHeaderElement.classList.add('sticky');
      placeholderElement.style.position = '';
    }
  },
  renderFeaturedFilters(facets, filterOptions) {
    context.desktopFilters.renderFeatured(
      facets,
      context.featuredFiltersContainer,
      {
        isMapInitiallyHidden: true,
      }
    );

    context.desktopFilters.renderBookingFilters(
      facets,
      context.bookingFiltersContainer
    );
    context.desktopFilters.renderGetawaysSearch(
      facets,
      context.getawaysSearchContainer
    );

    if (
      filterOptions.showExposedCategoriesDesktop &&
      context.exposedCategoriesContainer
    ) {
      context.desktopFilters.renderExposedCategories(
        context.exposedCategoriesContainer
      );
    }
  },
  updateFeaturedFilters(facets, filterOptions) {
    const shouldRerender =
      !context.featuredFiltersContainer ||
      !context.featuredFiltersContainer.innerHTML ||
      !context.featuredFiltersContainer.innerHTML.length;

    if (
      filterOptions.showExposedCategoriesDesktop &&
      context.exposedCategoriesContainer
    ) {
      context.desktopFilters.renderExposedCategories(
        context.exposedCategoriesContainer
      );
    }

    if (shouldRerender) {
      context.desktopFilters.renderFeatured(
        facets,
        context.featuredFiltersContainer,
        {
          isMapInitiallyHidden: true,
        }
      );
    } else {
      context.desktopFilters.updateFeatured(
        facets,
        context.featuredFiltersContainer
      );
    }

    const { shouldShowDateTimeFilter } = filterOptions || {};

    context.desktopFilters.updateBookingFilters({
      facets,
      shouldShowDateTimeFilter,
    });
  },
  handleFacetsFetchedSuccess(json) {
    if (!json) {
      context.clearPageHeader();
      context.clearFilters();
      return;
    }

    const { pageHeader, pageHeaderHash, facets, filterOptions, pagination } =
      json;

    context.handleUpdatePageHeader(pageHeader);

    if (!context.isNoResults(pagination) && facets) {
      if (context.domConfig.showCX90Desktop) {
        context.breadcrumbsContainer.innerHTML = '';
        context.pageHeaderTitleContainer.innerHTML = '';
        context.leftHandContainer.innerHTML = '';
        context.filtersHeaderRowContainer.innerHTML = '';

        context.desktopFilters.renderBreadcrumbs(
          facets,
          context.breadcrumbsContainer
        );

        context.desktopFilters.renderPageHeaderTitle(
          pageHeaderHash,
          context.pageHeaderTitleContainer
        );

        context.desktopFilters.renderLeftHandFilters(
          facets,
          context.leftHandContainer
        );
        context.desktopFilters.renderFiltersHeaderRow(
          facets,
          context.filtersHeaderRowContainer,
          {
            shouldShowMapRefinement: context.showMapButton,
          }
        );
        context.renderDealCount(pagination);
      } else {
        context.renderFeaturedFilters(facets, filterOptions);
      }
    } else {
      context.featuredFiltersContainer.innerHTML = '';
    }

    context.enableStickyFilters();
    document.body.classList.add('pull-app-filters-ui-js-lazy-loaded');
  },
  // eslint-disable-next-line no-unused-vars
  handleFacetsFetchedError(e) {},
  handleFacetsUpdatedSuccess(json) {
    if (!json) {
      context.clearPageHeader();
      context.clearFilters();
      return;
    }

    const {
      pageTitle,
      pageHeader,
      pageHeaderHash,
      facets,
      filterOptions,
      pagination,
    } = json;

    if (pageTitle) {
      document.title = pageTitle;
    }

    context.handleUpdatePageHeader(pageHeader);

    if (facets) {
      if (context.domConfig.showCX90Desktop) {
        const event = new CustomEvent('onNewRefinements', {
          detail: {
            facets,
          },
        });

        document.dispatchEvent(event);

        context.desktopFilters.renderPageHeaderTitle(
          pageHeaderHash,
          context.pageHeaderTitleContainer
        );
        context.renderDealCount(pagination);
      } else {
        context.updateFeaturedFilters(facets, filterOptions);
      }
    } else {
      context.clearFilters();
    }
  },
  handleFacetsUpdatedError() {},
  handleRefinementsUpdated(event) {
    // Temporary code so we don't have to change the old browse code
    // Can update the refinement selectedValues to the correct structure after we remove the old code
    if (
      event.detail &&
      event.detail.selectedRefinements &&
      event.detail.selectedRefinements.bbox
    ) {
      event.detail.selectedRefinements.bbox = {
        selectedValues: [{ id: event.detail.selectedRefinements.bbox }],
      };
    }
    const oldSearchParams = new URLSearchParams(location.search);
    const searchParams = createUrlSearchParams.create(event.detail);
    if (oldSearchParams.get(MAP_ID) === searchParams.get(MAP_ID)) {
      searchParams.delete(PAGE_PARAM);
    }

    const noMapCards = document.querySelector(SELECTORS.NO_MAP_CARDS);
    const shouldRemoveBbox = !searchParams.get(MAP_ID) && !!noMapCards;

    if (shouldRemoveBbox) {
      searchParams.delete(BBOX_ID);
    }

    let pathname = window.location.pathname;
    if (context.domConfig.isGoodsPage) {
      const goodsPermalink =
        searchParams.get(SUBCATEGORY_4_ID) ||
        searchParams.get(SUBCATEGORY_3_ID) ||
        searchParams.get(SUBCATEGORY_2_ID) ||
        searchParams.get(SUBCATEGORY_ID) ||
        searchParams.get(CATEGORY_ID);
      categoryParams.forEach(param => {
        searchParams.delete(param);
      });
      pathname = '/goods';
      if (goodsPermalink) {
        pathname += `/${goodsPermalink}`;
      }
    }

    const paramObject = createUrlSearchParams.paramsToObject(searchParams);
    const stringifiedSearchParams = searchParams.toString();
    const urlSearchParams = stringifiedSearchParams.length
      ? `?${stringifiedSearchParams}`
      : '';
    const updatedUrl = `${pathname}${urlSearchParams}`;

    urlUpdate.update(updatedUrl, { pushState: true });

    context.clearNoCardsMessage();

    if (noMapCards) {
      noMapCards.remove();
    }

    if (
      event.detail &&
      event.detail.options &&
      event.detail.options.isMapUpdate
    ) {
      paramObject.mapUpdate = true;
    }

    context.fetchCards(paramObject);
    context.dataFetcher
      .fetchFacets()
      .then(context.handleFacetsUpdatedSuccess)
      .catch(context.handleFacetsUpdatedError);
    context.handleMapState(paramObject);
  },
  handleUpdatePageHeader(pageHeader) {
    const pageHeaderElement = document.querySelector('.page-header');

    if (pageHeaderElement) {
      if (pageHeader) {
        pageHeaderElement.innerHTML = pageHeader;
      } else {
        context.clearPageHeader();
      }
    }
  },
  clearPageHeader() {
    const pageHeaderElement = document.querySelector('.page-header');

    if (pageHeaderElement) {
      pageHeaderElement.innerHTML = '';
    }
  },
  clearFilters() {
    const featuredFiltersContainer = document.getElementById(
      'featured-filters-ui-container'
    );
    if (featuredFiltersContainer) {
      featuredFiltersContainer.innerHTML = '';
    }
  },
  handleMapState(paramObject) {
    if (!paramObject.map) {
      context.mapContainerElement.classList.add('hide');
      return;
    } else {
      context.mapContainerElement.classList.remove('hide');
    }

    if (!window.BrowseMapsApplication) {
      import(
        /* webpackChunkName: "browse-maps" */ '../../../maps/frontend/application'
      ).then(BrowseMaps => {
        window.BrowseMapsApplication = BrowseMaps.default;
        context.onLazyLoadedMapsJavascript();
      });
    } else {
      window.BrowseMapsApplication.loadMap();
    }
  },
  onLazyLoadedMapsJavascript() {
    window.BrowseMapsApplication.initialize();
    window.BrowseMapsApplication.loadMap();
    document.body.classList.add('pull-app-maps-js-lazy-loaded');
  },
  hasNoCards() {
    const noCardsMessage = document.querySelector(SELECTORS.NO_CARDS);
    const noCardsGetways = document.querySelector(SELECTORS.NO_CARDS_GETAWAYS);

    return noCardsMessage && !noCardsGetways;
  },
  fetchLazyLoadedCards() {
    const cardShells = document.querySelector('.cui-shell-card');
    if (!cardShells) {
      return;
    }

    if (
      document.querySelector(SELECTORS.NO_CARDS_GETAWAYS) ||
      document.querySelector(SELECTORS.NO_CARDS_GOODS)
    ) {
      context.paginationRenderer.clearCardShells();
      return;
    }

    context.dataFetcher
      .fetchLazyLoadedCards()
      .then(context.handleLazyLoadedCardsFetchedSuccess)
      .catch(context.handleLazyLoadedCardsFetchedError);
  },
  fetchBottomContent() {
    context.dataFetcher
      .fetchBottomContent()
      .then(context.handleBottomContentFetchedSuccess)
      .catch(context.handleBottomContentFetchedError);
  },
  handleBottomContentFetchedSuccess(bottomContentResponse) {
    if (bottomContentResponse) {
      const { bottomContentHtml } = bottomContentResponse;
      context.paginationRenderer.insertBottomContent(bottomContentHtml);
    }
  },
  handleBottomContentFetchedError() {},
  updateUrlWhenNoCards() {
    if (context.hasNoCards()) {
      const updatedUrl = window.location.pathname;
      urlUpdate.update(updatedUrl);
    }
  },
  fetchCards(params) {
    const cardShells = document.querySelector('.cui-shell-card');
    if (cardShells) {
      return;
    }

    context.spinner.start();
    context.dataFetcher
      .fetchCards(params)
      .then(context.handlePaginatedCardsFetchedSuccess)
      .catch(context.handlePaginatedCardsFetchedError)
      .then(context.spinner.stop.bind(context.spinner));
  },
  handleLazyLoadedCardsFetchedError() {
    context.paginationRenderer.clearCardShells();
  },
  handleRestoreCardsFromState({ lazyLoadedCardsHtml, bottomContentHtml }) {
    if (!lazyLoadedCardsHtml) {
      return;
    }
    context.paginationRenderer.clearCardShells();
    context.paginationRenderer.restoreCardsHtmlFromState(lazyLoadedCardsHtml);
    context.paginationRenderer.restoreBottomContentHtmlFromState(
      bottomContentHtml
    );
    context.paginationRenderer.attachPaginationClickHandler(
      context.onPaginationClick
    );
    context.attachCardClickHandlers();
    context.arrangeCards();
  },
  handleLazyLoadedCardsFetchedSuccess(response) {
    context.paginationRenderer.clearCardShells();

    if (!response || !response.cardsHtml || !response.cardsHtml.length) {
      return;
    }

    context.showMapButton = response.showMapButton;
    context.updateMapButton();

    const scrollPos =
      window.scrollY ||
      document.documentElement.scrollTop ||
      document.body.scrollTop ||
      0;

    context.paginationRenderer.insertLazyLoadedCards(response);

    const { paginationHash } = response;
    const paginationContainer = document.querySelector(SELECTORS.PAGINATION);
    if (paginationHash && paginationContainer) {
      render(Pagination(paginationHash), paginationContainer);
    }
    window.scrollTo(0, scrollPos);
    context.paginationRenderer.attachPaginationClickHandler(
      context.onPaginationClick
    );

    context.attachCardClickHandlers();
    context.arrangeCards();
  },
  handlePaginatedCardsFetchedError() {},
  handlePaginatedCardsFetchedSuccess(response) {
    const {
      updateMapButton,
      paginationRenderer,
      attachCardClickHandlers,
      onPaginationClick,
    } = context;

    if (!response || !response.cardsHtml || !response.cardsHtml.length) {
      return;
    }

    context.showMapButton = response.showMapButton;
    updateMapButton();

    paginationRenderer.insertPaginatedCards(response);

    const { paginationHash } = response;
    const paginationContainer = document.querySelector(SELECTORS.PAGINATION);
    if (paginationHash && paginationContainer) {
      render(Pagination(response.paginationHash), paginationContainer);
    }
    paginationRenderer.scrollToTop();
    paginationRenderer.attachPaginationClickHandler(onPaginationClick);

    attachCardClickHandlers();
    context.arrangeCards();
    context.urlParams = Groupon.Utils.unparam();
  },
  hideElement(selector) {
    const el = document.querySelector(selector);
    if (el) {
      el.classList.add('hide');
    }
  },
  clearNoCardsMessage() {
    const noCardsMessage = document.querySelector(SELECTORS.NO_CARDS);
    if (noCardsMessage) {
      noCardsMessage.remove();
    }
  },
  onPaginationClick() {
    event.preventDefault();
    context.clearNoCardsMessage();

    const page = context.getPageNumberFromUrl(event.currentTarget.href);

    const paramObject = Groupon.Utils.unparam();
    paramObject.page = page;

    const updatedUrl = `${window.location.pathname}?${QueryParams.toString(
      paramObject
    )}`;
    urlUpdate.update(updatedUrl, { pushState: true });
    context.fetchCards(paramObject);
    context.handleMapState(paramObject);
  },
  attachCardClickHandlers() {
    const allCards = document.querySelectorAll(
      '.card-ui, .location-rememberance'
    );

    if (allCards && allCards.length) {
      allCards.forEach(card => {
        card.addEventListener('click', context.onCardClicked);
      });
    }

    const getawaysReset = document.querySelector('.getaways_reset');
    if (getawaysReset) {
      getawaysReset.addEventListener('click', context.performGetawaysReset);
    }
  },
  performGetawaysReset() {
    context.clearNoCardsMessage();
    if (context.desktopFilters && !context.domConfig.showCX90Desktop) {
      const resetOptions = {
        tryDifferentDate: true,
        update: true,
      };
      context.desktopFilters.resetRefinement('hotelAvailability', resetOptions);
    }
  },
  onCardClicked() {
    const lazyLoadedCardsHtml = context.sanitizeCardsHtml(
      document.querySelectorAll('#pull-deal-feed > *')
    );

    const bottomContentHtml = context.sanitizeCardsHtml(
      document.querySelectorAll('#pull-bottom-content')
    );

    const carouselScrollPositions =
      carouselScrollPositionsHelper.getCarouselScrollPositions();
    const scrollPosition = scrollPositionHelper.getScrollPosition();
    const showMapButton = context.showMapButton;

    context.setCardsAndScrollPositionInBrowserHistory({
      lazyLoadedCardsHtml,
      carouselScrollPositions,
      scrollPosition,
      showMapButton,
      bottomContentHtml,
    });
  },
  sanitizeCardsHtml(cardsHtml) {
    const combinedHtml = [];
    if (cardsHtml && cardsHtml.length) {
      cardsHtml.forEach(node => {
        if (node.classList.contains('ai-inventory-placement-container')) {
          node = node.cloneNode(true);
          adHelper.sanitizeAd(node);
        }

        const cardHtml = node.outerHTML.replace(
          /has-scroll-listener|has-viewportenter-listener|listener-added/g,
          ''
        );

        combinedHtml.push(cardHtml);
      });
    }
    return combinedHtml.join('');
  },
  restorePreviousState() {
    const state = (history.state && history.state.desktopBrowse) || {};
    const {
      lazyLoadedCardsHtml,
      carouselScrollPositions,
      scrollPosition,
      page,
      showMapButton,
      bottomContentHtml,
      bottomAds,
    } = state;

    if (
      lazyLoadedCardsHtml &&
      carouselScrollPositions &&
      scrollPosition != null &&
      page === context.getPageNumberFromUrl(window.location.href)
    ) {
      context.showMapButton = showMapButton;
      context.updateMapButton();

      context.handleRestoreCardsFromState({
        lazyLoadedCardsHtml,
        bottomContentHtml,
        bottomAds,
      });

      carouselScrollPositionsHelper.restoreCarouselScrollPositions(
        carouselScrollPositions
      );

      window.scrollTo(0, scrollPosition);
    } else {
      context.fetchBottomContent();

      if (context.hasNoCards()) {
        context.lazyloadPaginatedCards();
      } else {
        context.fetchLazyLoadedCards();
      }
    }

    context.setCardsAndScrollPositionInBrowserHistory();

    const paramObj = Groupon.Utils.unparam();
    context.handleMapState(paramObj);
  },
  lazyloadPaginatedCards() {
    const cardShells = document.querySelector('.cui-shell-card');
    if (!cardShells) {
      return;
    }

    const paramObject = {
      ...Groupon.Utils.unparam(),
    };

    context.dataFetcher
      .fetchCards(paramObject)
      .then(context.handleLazyLoadedCardsFetchedSuccess)
      .catch(context.handleLazyLoadedCardsFetchedError);
  },
  setCardsAndScrollPositionInBrowserHistory({
    lazyLoadedCardsHtml,
    carouselScrollPositions,
    scrollPosition,
    showMapButton,
    bottomContentHtml,
    bottomAds,
  } = {}) {
    const page = context.getPageNumberFromUrl(window.location.href);

    const state = {
      desktopBrowse: {
        lazyLoadedCardsHtml,
        carouselScrollPositions,
        scrollPosition,
        page,
        showMapButton,
        bottomContentHtml,
        bottomAds,
      },
    };

    history.replaceState(state, `Page ${page}`, window.location.href);
  },
  loadCoreCoupons() {
    CoreCoupons.init({
      showCoreCouponsLightbox: context.domConfig.showCoreCouponsLightbox,
    });
  },
  arrangeCards() {
    const cardsContainer = document.querySelector(SELECTORS.CARDS);
    arrangeCards(cardsContainer);
  },
};

export default context;
